import { Trans } from '@lingui/react/macro';
import { type ReactNode, useMemo } from 'react';

import { Button } from '@/components/buttons';
import { Text } from '@/components/typography';

import RateLimit from './RateLimitButton';

export type AuthMethodConfig = {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  rateLimit?: boolean;
};

type AuthMethodButtonsProps = {
  methods: AuthMethodConfig[];
  preferredMethodId?: string | null;
  methodLabels?: Record<string, string>;
};

const AuthMethodButtons = ({
  methods,
  preferredMethodId,
  methodLabels,
}: AuthMethodButtonsProps) => {
  const orderedMethods = useMemo(() => {
    if (!preferredMethodId) return methods;

    const preferredConfig = methods.find((method) => method.id === preferredMethodId);
    if (!preferredConfig) return methods;

    return [preferredConfig, ...methods.filter((method) => method.id !== preferredMethodId)];
  }, [methods, preferredMethodId]);

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4">
      {orderedMethods.map((method, index) => {
        const isFirst = index === 0;
        const isPreferred = preferredMethodId === method.id;
        const button = (
          <Button
            variant={isFirst ? 'primary' : 'secondary'}
            size="large"
            block
            onClick={method.onClick}
          >
            {method.icon}
            {method.label}
          </Button>
        );

        return (
          <div key={method.id} className="flex w-full flex-col items-center gap-2">
            {method.rateLimit ? <RateLimit>{button}</RateLimit> : button}
            {isPreferred && methodLabels?.[method.id] && (
              <Text variant="caption1" color="grey" className="text-center">
                <Trans>You used {methodLabels[method.id]} to sign in last time</Trans>
              </Text>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AuthMethodButtons;
